/**
 * Distinct failure classes that can surface on a thread. Add variants here
 * as new classifications are introduced — stored metrics are an array of these.
 */
export const THREAD_ERROR_TYPES = [
    'tool',
    'stream',
    'noReply',
    'emptyReply',
    'slowReply',
    'nudge',
    'loop',
    'multipleReplies',
    'moneyUnit',
] as const;

export type ThreadErrorType = (typeof THREAD_ERROR_TYPES)[number];

export type ThreadError =
    | { type: 'tool'; name: string }
    | { type: 'stream' }
    | { type: 'noReply' }
    | { type: 'emptyReply' }
    | { type: 'slowReply' }
    | { type: 'nudge' }
    | { type: 'loop' }
    | { type: 'multipleReplies' }
    | { type: 'moneyUnit' };

type SingletonThreadError = Exclude<ThreadError, { type: 'tool' }>;

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === 'object' && value != null && !Array.isArray(value);

export const isThreadError = (value: unknown): value is ThreadError => {
    if (!isRecord(value) || typeof value.type !== 'string') {
        return false;
    }

    switch (value.type) {
        case 'tool':
            return typeof value.name === 'string' && value.name.trim() !== '';
        case 'stream':
        case 'noReply':
        case 'emptyReply':
        case 'slowReply':
        case 'nudge':
        case 'loop':
        case 'multipleReplies':
        case 'moneyUnit':
            return true;
        default:
            return false;
    }
};

/** Dedupes tool errors by name and keeps at most one of each other type. */
export const toThreadErrors = (value: unknown): ThreadError[] => {
    if (!Array.isArray(value)) {
        return [];
    }

    const tools = new Map<string, Extract<ThreadError, { type: 'tool' }>>();
    const singletons = new Map<
        SingletonThreadError['type'],
        SingletonThreadError
    >();

    for (const item of value.filter(isThreadError)) {
        switch (item.type) {
            case 'tool':
                tools.set(item.name, item);
                break;
            case 'stream':
            case 'noReply':
            case 'emptyReply':
            case 'slowReply':
            case 'nudge':
            case 'loop':
            case 'multipleReplies':
            case 'moneyUnit':
                singletons.set(item.type, item);
                break;
            default: {
                const _exhaustive: never = item;
                return _exhaustive;
            }
        }
    }

    const toolErrors = [...tools.values()].sort((left, right) =>
        left.name.localeCompare(right.name),
    );
    const singletonErrors = THREAD_ERROR_TYPES.flatMap((type) => {
        if (type === 'tool') {
            return [];
        }

        const error = singletons.get(type);
        return error == null ? [] : [error];
    });

    return [...toolErrors, ...singletonErrors];
};

export const getThreadErrorKey = (error: ThreadError): string => {
    switch (error.type) {
        case 'tool':
            return `tool:${error.name}`;
        case 'stream':
            return 'stream';
        case 'noReply':
            return 'noReply';
        case 'emptyReply':
            return 'emptyReply';
        case 'slowReply':
            return 'slowReply';
        case 'nudge':
            return 'nudge';
        case 'loop':
            return 'loop';
        case 'multipleReplies':
            return 'multipleReplies';
        case 'moneyUnit':
            return 'moneyUnit';
        default: {
            const _exhaustive: never = error;
            return _exhaustive;
        }
    }
};
